import React, {ReactElement} from 'react';
import {cx} from '@emotion/css';
import {ReactComponent as PoundIcon} from '@fo/shared-images/growingPoundOnVase.svg';

import {getPageRoute} from '@fo/utils/routes/getPageRoute';
import {useRedirectToTide, ESource} from '@fo/shared-hooks/useRedirectToTide';

import DialogueCard from '../DialogueCard/DialogueCard';
import Button, {ButtonVariant} from '../Buttons/Button/Button';
import H, {HeaderType} from '../Text/Headers/Headers';
import P from '../Text/Paragraph/Paragraph';
import TrustpilotWidget from '../TrustpilotWidget/TrustpilotWidget';

import styles from './ArticleDialogueWithCta.styles';

export interface ArticleDialogueWithCtaProps {
  ctaButtonLabel: string;
  description?: string;
  href: string;
  isShowIcon?: boolean;
  isShowCloseButton?: boolean;
  title?: string;
  onClose?: () => void;
  shouldShowTrustpilot?: boolean;
  onClick?: () => void;
  isSkipQuoteFormAndRedirectToTide?: boolean;
}

function ArticleDialogWithCta({
  ctaButtonLabel,
  description,
  isShowCloseButton,
  isShowIcon,
  href,
  title,
  onClose,
  onClick,
  shouldShowTrustpilot,
  isSkipQuoteFormAndRedirectToTide,
}: ArticleDialogueWithCtaProps): ReactElement {
  const {redirectToTide} = useRedirectToTide();
  const buttonHref =
    href.includes(getPageRoute('quoteForm.start').replace(/\//g, '')) && isSkipQuoteFormAndRedirectToTide
      ? // set as undefined so the Button's onClick will be used instead
        undefined
      : // or, if it's not pointing to /get-started, use Contentful's button url
        href;

  return (
    <div className={cx(styles.container)}>
      <DialogueCard
        className={cx(styles.dialogue)}
        isHideCloseButton={!isShowCloseButton}
        onClose={onClose}
        name="articleDialogWithCta"
      >
        {isShowIcon && <PoundIcon className={cx(styles.icon)} data-testid="ui.articleDialogWithCta.icon" />}
        {title && (
          <H className={cx(styles.header)} headerType={HeaderType.H4} testId="ui.articleDialogWithCta.title">
            {title}
          </H>
        )}
        {description && (
          <P className={cx(styles.paragraph)} testId="ui.articleDialogWithCta.description">
            {description}
          </P>
        )}
        <Button
          href={buttonHref}
          ariaLabel={ctaButtonLabel}
          variant={ButtonVariant.CALL_TO_ACTION}
          className={cx(styles.button)}
          onClick={() => {
            if (onClick) {
              onClick();
            }
            if (isSkipQuoteFormAndRedirectToTide) {
              redirectToTide({source: ESource.FROM_CTA_TO_GET_STARTED});
            }
          }}
        >
          {ctaButtonLabel}
        </Button>
        {shouldShowTrustpilot && (
          <div className={cx(styles.trustpilot)}>
            <TrustpilotWidget className={cx(styles.trustpilotWidget)} />
          </div>
        )}
      </DialogueCard>
    </div>
  );
}

export default ArticleDialogWithCta;
